// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { RadioStyle } from "@insite/mobius/Radio";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { fontConfig } from "@stoneage/XDL/constants";
import { xBaseTheme } from "@stoneage/XDL/types";
import { color } from "@stoneage/XDL/Utilities";
import { css } from "styled-components";

const generalStyles = css`
    & + label {
        margin-left: 0;
        margin-right: 8px;
        align-items: flex-end;
    }
    & + label::before {
        flex: 1 0 14px;
        width: 14px;
        height: 14px;
        border-color: ${color("common.ui7")};
        margin-right: 6px;
    }
    &:checked + label::after {
        width: 13px;
        height: 13px;
        left: 0;
        top: 1px;
    }
    &:focus + label::before {
        box-shadow: none;
    }
`;

// The input itself is hidden (display: none), so hover is driven by its label
const hoverStyle = css`
    &:not(:disabled) + label:hover::before {
        border-color: ${color("primary")};
    }
    &:checked:not(:disabled) + label:hover::before {
        border-color: transparent;
        background: transparent;
    }
    &:not(:disabled) + label:hover {
        cursor: pointer;
    }
    &:checked:not(:disabled) + label:hover::after {
        border: 1px solid ${color("primary")};
        background: ${color("secondary")};
    }
`;

const checkedStyle = css`
    &:checked + label::before,
    &:not(:disabled):checked + label::before {
        border-color: transparent;
        background: transparent;
    }

    &:checked + label::after,
    &:not(:disabled):checked + label::after {
        background: ${color("secondary")};
        outline: 1px solid ${color("common.ui5")};
        outline-offset: -2px;
        border: 1px solid ${color("common.ui7")};
    }
`;

const disabledStyle = css`
    &:disabled + label {
        opacity: 1;
    }
    &:disabled + label::before {
        opacity: 0.33;
    }
    &:disabled + label::after {
        opacity: 0.33;
    }
`;

export default {
    defaultProps: {
        mergeCss: true,
        typographyProps: {
            mergeCss: true,
            size: fontConfig.xSmallSingleLine.size,
            weight: fontConfig.weight.regular,
            color: "text.main",
            css: css`
                letter-spacing: 0;
                line-height: ${fontConfig.xSmallSingleLine.lineHeight};
                user-select: none;
            `,
        },
        css: css`
            input {
                margin-left: 0;
                margin-right: 0;
                margin-top: 2px;
                width: 13px;
                height: 13px;
                display: none;
                ${generalStyles}
                ${hoverStyle}
                ${checkedStyle}
                ${disabledStyle}
            }
        `,
    },
    groupDefaultProps: {
        mergeCss: true,
        labelProps: {
            size: fontConfig.xSmall.size,
            weight: fontConfig.weight.regular,
            css: css`
                letter-spacing: 0;
                line-height: ${fontConfig.xSmall.lineHeight};
            `,
        },
        errorProps: {
            css: css`
                &:empty {
                    display: none;
                }
            `,
        },
        css: css`
            & > ${TypographyStyle as any} {
                margin-bottom: 12px;
                &:empty {
                    display: none;
                }
            }
            ${(props: any) => {
                return props.horizontal
                    ? css`
                          & > ${TypographyStyle as any} {
                              margin-bottom: 4px;
                          }
                          display: inline-block;
                          & > ${RadioStyle} {
                              display: inline-block;
                              margin-top: 0px;
                              margin-right: 16px;
                          }
                      `
                    : css`
                          display: flex;
                          flex-direction: column;
                          ${RadioStyle} {
                              display: flex;
                          }
                          ${RadioStyle} + ${RadioStyle} {
                              margin-top: 12px;
                          }
                      `;
            }}
        `,
    },
} as xBaseTheme["radio"];
